import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import {IconButton, Stack, Tooltip} from "@mui/material";
import Typography from "@mui/material/Typography";
import {ColumnDef} from "@tanstack/react-table";
import SimpleDialog from "crqlar-react-common/dist/components/SimpleDialog";
import {SurveyInvitation, SurveyInvitationStatus} from "crqlar-react-common/dist/models/survey/surveyInvitation.model";
import {SurveyService} from "crqlar-react-common/dist/services/survey/survey.service";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {enqueueSnackbar} from "notistack";
import {useMemo, useState} from "react";
import {
    ServerTable,
    ServerTableFrame,
    ServerTableProvider,
    useServerTable
} from "../../common/serverTable";
import {DateElement} from "../../common/uiElements/DateElement";
import SurveyInvitationStatusChip from "../invitations/SurveyInvitationStatusChip";
import {invitationSurveyLink} from "../shared/surveyPublicLinks.util";
import {SURVEY_GUEST_INVITATIONS_TABLE_ID, useSurveyInvitationRefresh} from "../shared/surveyQueryRefresh";

interface Props {
    reservationId?: string
    profileId?: string
}

const ALL_INVITATION_STATUSES = Object.values(SurveyInvitationStatus);

/** Five rows keep both share-dialog tables, and the link area above them, free of an inner scrollbar. */
const PAGE_SIZE = 5;

const copyInvitationLink = async (invitation: SurveyInvitation, copiedLabel: string) => {
    const link = invitation.token ? invitationSurveyLink(invitation.token) : '';
    if (!link) return;

    try {
        await navigator.clipboard.writeText(link);
        enqueueSnackbar(copiedLabel, {
            variant: 'success',
            anchorOrigin: {vertical: 'bottom', horizontal: 'center'}
        });
    } catch (error) {
        console.log(error);
    }
};

/**
 * A guest's invitations inside the share dialog: the real table, in the `embedded` preset -
 * no toolbar, fixed columns, fixed page size, and the framework's own pagination footer.
 *
 * The `tableId` is shared with the full invitations history on purpose. It is what puts this
 * table's query key under the same prefix `useSurveyInvitationRefresh()` invalidates, and the
 * embedded preset is what makes sharing it safe: no column preferences are stored under it.
 */
const SurveyShareInvitationsTable = ({reservationId, profileId}: Props) => {
    const {t} = useLocalize();
    const readOnly = useReadOnly();
    const refreshInvitations = useSurveyInvitationRefresh();
    const [voidTarget, setVoidTarget] = useState<SurveyInvitation | null>(null);
    const [voiding, setVoiding] = useState(false);

    const columns = useMemo<ColumnDef<SurveyInvitation>[]>(() => [
        {
            id: 'surveyName',
            header: t('Survey'),
            accessorFn: invitation => invitation.surveyName,
            cell: ({row}) => (
                <Typography variant={'body1'} noWrap>{row.original.surveyName || '—'}</Typography>
            ),
            size: 240,
            meta: {grow: true}
        },
        {
            id: 'sentAt',
            header: t('Sent'),
            accessorFn: invitation => invitation.sentAt,
            cell: ({row}) => <DateElement date={row.original.sentAt}/>,
            size: 170
        },
        {
            id: 'status',
            header: t('Status'),
            accessorFn: invitation => invitation.status,
            cell: ({row}) => (
                <SurveyInvitationStatusChip
                    status={row.original.status}
                    furthestQuestionIndex={row.original.furthestQuestionIndex}
                />
            ),
            size: 190
        },
        {
            id: 'actions',
            header: t('Actions'),
            cell: ({row}) => {
                const invitation = row.original;
                const canCopy = !!invitation.token && invitation.status !== SurveyInvitationStatus.VOIDED;
                const canVoid = !readOnly
                    && invitation.status !== SurveyInvitationStatus.COMPLETED
                    && invitation.status !== SurveyInvitationStatus.VOIDED;

                return (
                    <Stack
                        direction={'row'}
                        alignItems={'center'}
                        justifyContent={'flex-end'}
                        gap={0.5}
                        data-no-row-click
                    >
                        {canCopy && (
                            <Tooltip title={t('Copy link')}>
                                <IconButton
                                    size={'small'}
                                    onClick={() => void copyInvitationLink(invitation, t('Copied'))}
                                >
                                    <ContentCopyIcon fontSize={'small'}/>
                                </IconButton>
                            </Tooltip>
                        )}

                        {canVoid && (
                            <Tooltip title={t('Void link')}>
                                <IconButton size={'small'} onClick={() => setVoidTarget(invitation)}>
                                    <LinkOffIcon fontSize={'small'}/>
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                );
            },
            size: 96,
            meta: {align: 'right'}
        }
    ], [readOnly, t]);

    const controller = useServerTable<SurveyInvitation>({
        tableId: SURVEY_GUEST_INVITATIONS_TABLE_ID,
        presentation: 'embedded',
        columns,
        getRowId: invitation => invitation.id,
        defaultPageSize: PAGE_SIZE,
        enabled: !!profileId || !!reservationId,
        queryDependencies: [profileId, reservationId],
        // Newest first, fixed: sorting here rather than through `initialSorting` keeps the
        // header free of the sort and remove-sort controls this dialog has no room for.
        queryFn: async ({pagination}) => {
            const page = await SurveyService.invitationList({
                reservationId,
                profileId,
                statuses: ALL_INVITATION_STATUSES,
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: [{field: 'sentAt', direction: 'DESC'}]
                }
            });

            return {
                content: page.content ?? [],
                totalElements: page.totalElements ?? 0
            };
        }
    });

    const voidInvitation = async () => {
        if (!voidTarget?.id) return;

        setVoiding(true);
        try {
            await SurveyService.invitationVoid({id: voidTarget.id});
            setVoidTarget(null);
            await refreshInvitations();
            enqueueSnackbar(t('Link voided'), {
                variant: 'success',
                anchorOrigin: {vertical: 'bottom', horizontal: 'center'}
            });
        } catch (error) {
            console.log(error);
            enqueueSnackbar(t('This link could not be voided.'), {
                variant: 'error',
                anchorOrigin: {vertical: 'bottom', horizontal: 'center'}
            });
        } finally {
            setVoiding(false);
        }
    };

    return (
        <>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>

            <SimpleDialog
                open={!!voidTarget}
                onClose={() => setVoidTarget(null)}
                title={t('Void this link?')}
                message={t('The guest can no longer open this survey with it. The invitation stays in the list.')}
                confirmText={voiding ? t('Voiding...') : t('Void link')}
                cancelText={t('Cancel')}
                onConfirm={voidInvitation}
                onCancel={() => setVoidTarget(null)}
            />
        </>
    );
};

export default SurveyShareInvitationsTable;
